import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Send,
  CheckCircle2,
  Package,
  Archive,
  KeyRound,
  AlertTriangle,
  ExternalLink,
  Image,
  X,
  Receipt,
  Plus,
  Minus,
  ShoppingCart,
  Pencil,
  Trash2,
  ChevronDown,
  Camera,
} from "lucide-react";
import { useInventoryForCleaner, useSubmitInventory } from "@/hooks/useCleaner";
import { sendInventoryLowStockEmail } from "@/lib/notificationEmails";
import { notifyOpsHub } from "@/lib/notifyOpsHub";
import type { TurnoverWizardStepId } from "@/lib/turnoverWizardPlan";
import { useInventoryRoomsForCleaner } from "@/hooks/useInventoryRoomsForCleaner";
import { usePropertyBillingSettings, useReimbursementsByTask } from "@/hooks/useInventoryReimbursements";
import { useAuth } from "@/contexts/AuthContext";
import { useCleanerRecord } from "@/hooks/useCleaner";
import {
  useTurnoverPurchases,
  useDeleteCleanerPurchase,
  type TurnoverPurchase,
} from "@/hooks/useCleanerPurchases";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { FULLNESS_OPTIONS, getFullnessLabel } from "@/hooks/useInventory";
import { getInStockCount, getOrderedCount } from "@/lib/inventoryQuantity";
import InventoryItemSheet from "./InventoryItemSheet";
import { ReimbursementModal } from "./ReimbursementModal";
import { PurchaseModeModal } from "./PurchaseModeModal";
import { MultiItemPurchaseSheet } from "./MultiItemPurchaseSheet";
import { PurchaseItemPickerSheet } from "./PurchaseItemPickerSheet";
import { EditTurnoverPurchaseSheet } from "./EditTurnoverPurchaseSheet";
import { cn } from "@/lib/utils";

export type SubmitInventoryParams = {
  propertyId: string;
  items: { item_id: string; item_name: string; count: number; fullness_value?: number }[];
  cleaningTaskId?: string | null;
  /** When true, only update item rows; do not set inventory_completed on task (avoids auto-ending clean). */
  onlyUpdateItems?: boolean;
  inventorySubmitPhase?: "guest" | "final";
  closeout?: {
    keys_returned_confirmed: boolean;
    departure_notes?: string | null;
    keys_photo_url?: string | null;
  };
  /** Counts already saved; only write closeout flags on the task. */
  closeoutOnly?: boolean;
};

function inventorySubmitErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim()) return err.message;
  if (err && typeof err === "object" && "message" in err) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return fallback;
}

interface InventoryCheckTabProps {
  propertyId: string;
  cleanerId?: string;
  reimbursementMode?: string;
  readOnly?: boolean;
  onInventorySubmitted?: () => void;
  /** Called after inventory is submitted, to mark the cleaning task complete (only when user clicks Complete Cleaning). */
  onCompleteCleaning?: () => Promise<void>;
  /** When true, Complete Cleaning button is disabled until all checklist tasks are done. */
  allTasksCompleted?: boolean;
  /** When set (e.g. from task flow), reimbursements are attached to this task and its invoice period */
  cleaningTaskId?: string | null;
  scheduledDate?: string | null;
  /** Server flag: guest-amenities inventory phase saved */
  inventoryGuestPhaseCompleted?: boolean | null;
  /** When false and guest rows or checklist staging exist, block guest-phase submit until guest amenity staging is done on the task. */
  guestSetupCompleted?: boolean;
  /** Checklist staging line count; when > 0, guest phase and submit behave like guest inventory rows (setup + phase gate). */
  guestStagingLineCount?: number;
  /** When set from turnover wizard, scrolls the inventory card into view for the active inventory step. */
  wizardInventoryStepId?: TurnoverWizardStepId;
  /** Slice 4: keys already handled in SimpleKeysStep — skip keys sub-step and submit from counts. */
  keysHandledExternally?: boolean;
  /** Keys photo URL when keysHandledExternally (from task.closeout_keys_photo_url). */
  externalKeysPhotoUrl?: string | null;
  /** When set, used instead of useSubmitInventory (for owner/team task runner). */
  submitInventoryAsOwnerOrTeam?: { mutateAsync: (params: SubmitInventoryParams) => Promise<unknown>; isPending: boolean };
  /** e.g. property name · unit — shown under the page title on mobile. */
  propertySubtitle?: string | null;
}

interface ItemCount {
  item_id: string;
  item_name: string;
  count: number;
}

interface InventoryItem {
  id: string;
  item_name: string;
  in_stock_qty?: number | null;
  ordered_qty?: number | null;
  current_count?: number | null;
  threshold_count: number | null;
  vendor_url?: string | null;
  vendor_label?: string | null;
  photo_url?: string | null;
  room_id?: string | null;
  quantity_type?: string | null;
  current_value?: number | null;
  threshold_value?: number | null;
  inventory_scope?: string | null;
}

/** Threshold null => no low state (OK). */
const getItemStatus = (item: InventoryItem) => {
  if (item.quantity_type === 'fullness') {
    const c = item.current_value ?? 0;
    const t = item.threshold_value ?? 0.25;
    if (c === 0) return "out";
    if (c <= t) return "low";
    return "ok";
  }
  const c = getInStockCount(item);
  const t = item.threshold_count;
  if (t == null) return "ok"; // No threshold set => OK
  if (c === 0) return "out";
  if (c <= t) return "low";
  return "ok";
};

/** Display-only status indicator (non-interactive). */
const StatusBadge = ({ status, tDynamic }: { status: "ok" | "low" | "out"; tDynamic: (text: string) => string }) => {
  if (status === "ok") {
    return (
      <Badge
        variant="secondary"
        className="shrink-0 cursor-default border border-[color:var(--pv-teal,#0fa89a)]/25 bg-[color:var(--pv-teal-soft,rgb(220_252_231))] text-[color:var(--pv-teal-hover,#0b8e83)] pointer-events-none"
      >
        {tDynamic("OK")}
      </Badge>
    );
  }
  if (status === "low") {
    return (
      <Badge
        variant="secondary"
        className="shrink-0 cursor-default border border-[color:var(--pv-warn,#c26a1a)]/35 bg-[color:var(--pv-warn-soft,#fbf0e1)] text-[color:var(--pv-warn,#c26a1a)] pointer-events-none font-semibold"
      >
        <AlertTriangle className="mr-1 h-3 w-3 shrink-0" aria-hidden />
        {tDynamic("LOW")}
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className="shrink-0 cursor-default border border-[color:var(--pv-error,#c0392b)]/30 bg-red-50 text-[color:var(--pv-error,#b91c1c)] dark:bg-red-950/40 dark:text-red-200 pointer-events-none font-semibold"
    >
      {tDynamic("Out")}
    </Badge>
  );
};

function getTurnoverPurchaseStatusLabel(
  p: { status: string; type: string; payment_failed?: boolean | null },
  tDynamic: (s: string) => string
): string {
  if (p.status === "pending_approval") {
    if ((p as { payment_failed?: boolean }).payment_failed) return tDynamic("Needs proof");
    return tDynamic("Pending");
  }
  if (p.status === "approved" && (p as { payment_failed?: boolean }).payment_failed) return tDynamic("Payment failed");
  switch (p.status) {
    case "auto_approved":
    case "paid":
      return tDynamic("Auto-paid");
    case "approved":
      return tDynamic("Approved");
    case "rejected":
      return tDynamic("Rejected");
    default:
      return p.status;
  }
}

const InventoryCheckTab = ({ 
  propertyId, 
  cleanerId = "",
  reimbursementMode = "auto_pay",
  readOnly = false,
  onInventorySubmitted,
  onCompleteCleaning,
  allTasksCompleted = true,
  cleaningTaskId,
  scheduledDate,
  inventoryGuestPhaseCompleted = false,
  guestSetupCompleted = true,
  guestStagingLineCount = 0,
  submitInventoryAsOwnerOrTeam,
  wizardInventoryStepId,
  keysHandledExternally = false,
  externalKeysPhotoUrl = null,
  propertySubtitle = null,
}: InventoryCheckTabProps) => {
  const { tDynamic } = useTranslation();
  const { user } = useAuth();
  const { data: cleaner } = useCleanerRecord();
  const { data: items, isLoading } = useInventoryForCleaner(propertyId);
  const { data: rooms, isLoading: roomsLoading } = useInventoryRoomsForCleaner(propertyId);
  const { data: billingSettings } = usePropertyBillingSettings(propertyId);
  const { data: taskReimbursements = [] } = useReimbursementsByTask(cleaningTaskId ?? undefined);
  const { data: turnoverPurchases = [] } = useTurnoverPurchases(cleaningTaskId ?? undefined);
  /** Latest request_order row per inventory item for this task (for "Request made" UI). */
  const requestOrderByItemId = useMemo(() => {
    const m = new Map<string, TurnoverPurchase>();
    for (const p of turnoverPurchases) {
      if (p.type !== "request_order") continue;
      if (p.item_id) {
        if (!m.has(p.item_id)) m.set(p.item_id, p);
      }
      const lines = p.line_items;
      if (lines?.length) {
        for (const li of lines) {
          if (!m.has(li.inventory_item_id)) m.set(li.inventory_item_id, p);
        }
      }
    }
    return m;
  }, [turnoverPurchases]);
  const deletePurchase = useDeleteCleanerPurchase();
  const submitInventoryDefault = useSubmitInventory();
  const submitInventory = submitInventoryAsOwnerOrTeam ?? submitInventoryDefault;

  const postCleanItems = useMemo(() => {
    if (!items) return [] as InventoryItem[];
    return (items as InventoryItem[]).filter((i) => i.inventory_scope !== "unit");
  }, [items]);

  const guestScopeItems = useMemo(
    () =>
      postCleanItems.filter(
        (i) => !i.inventory_scope || i.inventory_scope === "guest_amenities"
      ),
    [postCleanItems]
  );

  const supplyScopeItems = useMemo(
    () => postCleanItems.filter((i) => i.inventory_scope === "supply_closet"),
    [postCleanItems]
  );

  const guestStagingLineCountSafe = Math.max(0, guestStagingLineCount);
  const hasGuestPhaseInventoryOrStaging =
    guestScopeItems.length > 0 || guestStagingLineCountSafe > 0;

  /** Legacy guest→supply split is disabled: one combined count list, then a keys sub-step. */

  const taskPhasedMode = Boolean(cleaningTaskId) && !readOnly;

  /** After counts, cleaner confirms keys on a dedicated sub-step (same wizard step). */
  const [inventoryUiPhase, setInventoryUiPhase] = useState<"counts" | "keys">("counts");

  useEffect(() => {
    setInventoryUiPhase("counts");
  }, [cleaningTaskId]);

  type InventoryFlowMode = "legacy" | "single" | "split_guest" | "split_supply";

  /** Visual mode: phased turnover always uses the single combined list. */
  const inventoryFlowMode = useMemo((): InventoryFlowMode => {
    if (!taskPhasedMode) return "legacy";
    return "single";
  }, [taskPhasedMode]);

  const itemsForPhase = useMemo(() => {
    if (!taskPhasedMode) return (items ?? []) as InventoryItem[];
    return postCleanItems;
  }, [taskPhasedMode, items, postCleanItems]);

  const hidePhasedEmptyInventoryCard = useMemo(
    () => taskPhasedMode && inventoryUiPhase === "keys",
    [taskPhasedMode, inventoryUiPhase]
  );

  const showPurchasesAndCleanerBuys = true;

  const hidePhasedInstructionBanner = false;

  const displaySource = useMemo(() => {
    if (!items) return [] as InventoryItem[];
    const list = items as InventoryItem[];
    const scopeOrder = (s: string | null | undefined) =>
      s === "guest_amenities" ? 0 : s === "supply_closet" ? 2 : 1;
    const sorted = (arr: InventoryItem[]) =>
      [...arr].sort((a, b) => scopeOrder(a.inventory_scope) - scopeOrder(b.inventory_scope));
    if (!cleaningTaskId) return sorted(list);
    if (readOnly) return sorted(list.filter((i) => i.inventory_scope !== "unit"));
    return sorted(itemsForPhase);
  }, [items, cleaningTaskId, readOnly, itemsForPhase]);

  const [counts, setCounts] = useState<ItemCount[]>([]);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  /** When opening the sheet from row: "request" = request-only form, "purchase" = purchase-only form */
  const [sheetInitialMode, setSheetInitialMode] = useState<"request" | "purchase" | "idle">("idle");
  const [fullscreenPhoto, setFullscreenPhoto] = useState<string | null>(null);
  const [reimbursementModalOpen, setReimbursementModalOpen] = useState(false);
  const [reimbursementPrefillName, setReimbursementPrefillName] = useState("");
  const [showLowStockFinishConfirm, setShowLowStockFinishConfirm] = useState(false);
  const [purchaseModeModalOpen, setPurchaseModeModalOpen] = useState(false);
  const [multiItemSheetOpen, setMultiItemSheetOpen] = useState(false);
  const [purchaseItemPickerOpen, setPurchaseItemPickerOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<TurnoverPurchase | null>(null);
  const [removeConfirmPurchase, setRemoveConfirmPurchase] = useState<TurnoverPurchase | null>(null);
  const [keysReturnedConfirmed, setKeysReturnedConfirmed] = useState(false);
  const [closeoutNotes, setCloseoutNotes] = useState("");
  const [closeoutKeysPhotoUrl, setCloseoutKeysPhotoUrl] = useState("");

  const inventoryPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wizardInventoryStepId && !taskPhasedMode) return;
    requestAnimationFrame(() => {
      inventoryPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [wizardInventoryStepId, taskPhasedMode, inventoryUiPhase]);

  const inventoryAutopayEnabled = billingSettings?.inventory_autopay_enabled ?? false;
  const billToType =
    billingSettings?.default_bill_to_type && billingSettings.default_bill_to_type !== "none"
      ? billingSettings.default_bill_to_type
      : billingSettings?.billing_mode === "company" && cleaner?.company_id
        ? "owner_to_company"
        : cleaner?.id
          ? "owner_to_cleaner"
          : "none";
  const billToId =
    billingSettings?.default_bill_to_id ??
    (billToType === "owner_to_company" ? cleaner?.company_id ?? null : billToType === "owner_to_cleaner" ? cleaner?.id ?? null : null);

  useEffect(() => {
    if (items) {
      setCounts(
        items.map((item) => ({
          item_id: item.id,
          item_name: item.item_name,
          count: getInStockCount(item as InventoryItem),
        }))
      );
    }
  }, [items]);

  // Group items by room (post-clean phases for active task; unit items excluded in task flow)
  const groupedItems = useMemo(() => {
    if (!displaySource.length) return { unassigned: [] as InventoryItem[], byRoom: {} as Record<string, InventoryItem[]> };

    const byRoom: Record<string, InventoryItem[]> = {};
    const unassigned: InventoryItem[] = [];

    displaySource.forEach((item) => {
      const typedItem = item as InventoryItem;
      if (!item.room_id) {
        unassigned.push(typedItem);
      } else {
        if (!byRoom[item.room_id]) byRoom[item.room_id] = [];
        byRoom[item.room_id].push(typedItem);
      }
    });

    return { unassigned, byRoom };
  }, [displaySource]);

  // Check if a room has low stock items
  const getRoomSeverityCounts = (roomItems: InventoryItem[]) => {
    let low = 0;
    let out = 0;
    for (const item of roomItems) {
      const s = getItemStatus(item);
      if (s === "low") low++;
      else if (s === "out") out++;
    }
    return { total: roomItems.length, low, out };
  };

  const [fullnessValues, setFullnessValues] = useState<Record<string, number>>({});
  /** Tracks which items have been accounted for (updated count/fullness or "No change in count"). */
  const [accountedFor, setAccountedFor] = useState<Record<string, "updated" | "no_change">>({});

  useEffect(() => {
    if (items) {
      const initialFullness: Record<string, number> = {};
      items.forEach(item => {
        if (item.quantity_type === 'fullness') {
          initialFullness[item.id] = item.current_value ?? 1.0;
        }
      });
      setFullnessValues(initialFullness);
    }
  }, [items]);

  const handleFullnessChange = async (itemId: string, value: number) => {
    setFullnessValues(prev => ({ ...prev, [itemId]: value }));
    setAccountedFor(prev => ({ ...prev, [itemId]: "updated" }));

    // Save item only; do not touch task (avoids auto-ending the clean)
    const item = items?.find(i => i.id === itemId);
    if (!item) return;
    
    try {
      await submitInventory.mutateAsync({
        propertyId,
        items: [{
          item_id: itemId,
          item_name: item.item_name,
          count: getInStockCount(item as InventoryItem),
          fullness_value: value,
        }],
        cleaningTaskId: cleaningTaskId ?? undefined,
        onlyUpdateItems: true,
      });
      toast({ 
        title: tDynamic("Updated"), 
        description: `${item.item_name}: ${getFullnessLabel(value)}` 
      });
    } catch (error) {
      toast({ 
        title: tDynamic("Error"), 
        description: tDynamic("Failed to update"), 
        variant: "destructive" 
      });
    }
  };
  const updateCount = (item_id: string, count: number) => {
    setCounts((prev) =>
      prev.map((item) => (item.item_id === item_id ? { ...item, count } : item))
    );
    setAccountedFor((prev) => ({ ...prev, [item_id]: "updated" }));
  };

  const markNoChange = (item_id: string) => {
    setAccountedFor((prev) => ({ ...prev, [item_id]: "no_change" }));
    toast({ title: tDynamic("No change in count"), description: tDynamic("Marked as no change") });
  };

  const openRequestSheet = (item: InventoryItem) => {
    setSelectedItem(item);
    setSheetInitialMode("request");
    setSheetOpen(true);
  };
  const openPurchaseSheet = (item: InventoryItem) => {
    setSelectedItem(item);
    setSheetInitialMode("purchase");
    setSheetOpen(true);
  };
  const handlePurchaseModeSelect = (mode: "multi" | "single") => {
    if (mode === "multi") setMultiItemSheetOpen(true);
    else setPurchaseItemPickerOpen(true);
  };
  const handlePurchaseItemPick = (item: InventoryItem) => {
    setSelectedItem(item);
    setSheetInitialMode("purchase");
    setSheetOpen(true);
  };

  const buildSubmitRows = useCallback(
    (list: InventoryItem[]) =>
      list.map((item) => {
        if (item.quantity_type === "fullness") {
          const value = fullnessValues[item.id] ?? item.current_value ?? 1;
          return { item_id: item.id, item_name: item.item_name, count: 0, fullness_value: value };
        }
        const c = counts.find((x) => x.item_id === item.id);
        return {
          item_id: item.id,
          item_name: item.item_name,
          count: c?.count ?? getInStockCount(item as InventoryItem),
        };
      }),
    [counts, fullnessValues]
  );

  /** Phased turnover: save counts to `inventory_items` when leaving counts for keys (host portal updates immediately). Final submit still inserts submission + closeout. */
  const persistPostCleanCountsOnly = useCallback(async () => {
    const rows = buildSubmitRows(postCleanItems);
    if (rows.length === 0) return;
    await submitInventory.mutateAsync({
      propertyId,
      items: rows,
      cleaningTaskId: cleaningTaskId ?? undefined,
      onlyUpdateItems: true,
    });
    countsPersistedRef.current = true;
  }, [buildSubmitRows, postCleanItems, propertyId, cleaningTaskId, submitInventory]);

  /** Items that are below threshold with current form state (for finish confirmation). */
  const getLowStockItemsForFinish = useMemo(() => {
    if (!items) return [];
    const pool = cleaningTaskId
      ? (items as InventoryItem[]).filter((i) => i.inventory_scope !== "unit")
      : (items as InventoryItem[]);
    return pool.filter((item) => {
      if (item.quantity_type === "fullness") {
        if (item.threshold_value == null) return false;
        const current = fullnessValues[item.id] ?? item.current_value ?? 1;
        return current <= item.threshold_value;
      }
      if (item.threshold_count == null) return false;
      const countState = counts.find((c) => c.item_id === item.id);
      const current = countState?.count ?? getInStockCount(item as InventoryItem);
      return current < item.threshold_count;
    });
  }, [items, counts, fullnessValues, cleaningTaskId]);

  /** Low-stock within the rows visible for the current inventory step (guest vs supply). */
  const getLowStockItemsCurrentPhase = useMemo(() => {
    if (!displaySource.length) return [];
    return displaySource.filter((item) => {
      if (item.quantity_type === "fullness") {
        if (item.threshold_value == null) return false;
        const current = fullnessValues[item.id] ?? item.current_value ?? 1;
        return current <= item.threshold_value;
      }
      if (item.threshold_count == null) return false;
      const countState = counts.find((c) => c.item_id === item.id);
      const current = countState?.count ?? getInStockCount(item as InventoryItem);
      return current < item.threshold_count;
    });
  }, [displaySource, counts, fullnessValues]);

  const allInventoryAccountedFor = useMemo(() => {
    if (!displaySource.length) return true;
    return displaySource.every((item) => accountedFor[item.id] != null);
  }, [displaySource, accountedFor]);

  const needsCloseoutKeys = taskPhasedMode && inventoryUiPhase === "keys";
  const guestStagingGateOk =
    !hasGuestPhaseInventoryOrStaging || guestSetupCompleted !== false;
  const canCompleteCleaning =
    allTasksCompleted &&
    (inventoryUiPhase === "keys"
      ? keysReturnedConfirmed
      : allInventoryAccountedFor && guestStagingGateOk);

  /**
   * Low-stock digest buffer.
   *
   * A turnover can have up to two inventory submits (guest + supply) and an
   * empty-closeout finish. We collect low-stock detections from every phase
   * here, then send ONE email and write ONE owner notification at the very
   * end of the turnover (via `flushLowStockDigest`). Dedupe is by item_id so
   * an item that's low in both phases only shows once.
   */
  type LowStockDigestLine = {
    item_id: string;
    item_name: string;
    detail: string;
    scopes: Set<string>;
  };
  const lowStockDigestRef = useRef<Map<string, LowStockDigestLine>>(new Map());
  /** True after counts were saved on the counts→keys transition (avoid duplicate item writes at closeout). */
  const countsPersistedRef = useRef(false);

  useEffect(() => {
    countsPersistedRef.current = false;
  }, [cleaningTaskId]);

  const buildLowStockLines = useCallback(
    (pool: InventoryItem[]): { item_id: string; item_name: string; detail: string }[] => {
      return pool
        .filter((item) => {
          if (item.quantity_type === "fullness") {
            if (item.threshold_value == null) return false;
            const current = fullnessValues[item.id] ?? item.current_value ?? 1;
            return current <= item.threshold_value;
          }
          if (item.threshold_count == null) return false;
          const countState = counts.find((c) => c.item_id === item.id);
          const current = countState?.count ?? getInStockCount(item as InventoryItem);
          return current < item.threshold_count;
        })
        .map((item) => ({
          item_id: item.id,
          item_name: item.item_name,
          detail:
            item.quantity_type === "fullness"
              ? `${getFullnessLabel(fullnessValues[item.id] ?? item.current_value ?? 1)} (threshold ${getFullnessLabel(item.threshold_value ?? 0)})`
              : `Count ${counts.find((c) => c.item_id === item.id)?.count ?? getInStockCount(item as InventoryItem)} (threshold ${item.threshold_count})`,
        }));
    },
    [fullnessValues, counts]
  );

  const runLowStockEmailForItems = (pool: InventoryItem[], scopeLabel: string) => {
    if (!cleaningTaskId || pool.length === 0) return;
    const lines = buildLowStockLines(pool);
    if (lines.length === 0) return;
    const map = lowStockDigestRef.current;
    for (const line of lines) {
      const existing = map.get(line.item_id);
      if (existing) {
        existing.detail = line.detail;
        existing.scopes.add(scopeLabel);
      } else {
        map.set(line.item_id, {
          item_id: line.item_id,
          item_name: line.item_name,
          detail: line.detail,
          scopes: new Set([scopeLabel]),
        });
      }
    }
  };

  /**
   * Send the digest at the end of the turnover: one email + one owner_notifications row.
   * Safe to call when nothing was queued (it returns early).
   */
  const flushLowStockDigest = async () => {
    const map = lowStockDigestRef.current;
    if (!cleaningTaskId || map.size === 0) {
      map.clear();
      return;
    }
    const dedupedLines = Array.from(map.values()).map((l) => ({
      item_name: l.item_name,
      detail: l.detail,
    }));
    const allScopes = Array.from(
      new Set(Array.from(map.values()).flatMap((l) => Array.from(l.scopes)))
    );
    const scopeLabel = allScopes.length === 1 ? allScopes[0] : "Post-clean inventory (digest)";

    // 1) Send ONE email.
    const res = await sendInventoryLowStockEmail({
      propertyId,
      cleaningTaskId,
      lines: dedupedLines,
      scopeLabel,
    });
    if (!res.ok) {
      console.warn("[InventoryCheckTab] Low-stock digest email:", res.error);
    }

    // 2) Insert ONE owner_notifications row (in-app inbox) listing every item.
    try {
      const { data: property } = await supabase
        .from("properties")
        .select("owner_id, name")
        .eq("id", propertyId)
        .single();
      if (property?.owner_id) {
        const bulletList = dedupedLines
          .map((l) => `• ${l.item_name} — ${l.detail}`)
          .join("\n");
        const title = `Low stock after turnover at ${property.name ?? "your property"}`;
        const message = `${dedupedLines.length} item(s) are at or below threshold after this clean.\n\n${bulletList}\n\nScope: ${scopeLabel}`;
        await supabase.from("owner_notifications").insert({
          owner_id: property.owner_id,
          property_id: propertyId,
          cleaning_task_id: cleaningTaskId,
          type: "inventory_low_stock",
          title,
          message,
        });
      }
    } catch (err) {
      console.warn("[InventoryCheckTab] Low-stock inbox insert failed:", err);
    }

    // 3) Open Admin OPS reorder tasks (non-blocking).
    void notifyOpsHub({
      event: "inventory_low_stock",
      propertyId,
      cleaningTaskId,
      items: dedupedLines,
      detail: scopeLabel,
    }).then((r) => {
      if (!r.ok && !r.skipped) {
        console.warn("[InventoryCheckTab] Ops Hub notify:", r.error);
      }
    });

    map.clear();
  };

  /** @deprecated use runLowStockEmailForItems with explicit pool */
  const runLowStockEmail = () => {
    const pool = cleaningTaskId
      ? (items as InventoryItem[]).filter((i) => i.inventory_scope !== "unit")
      : (items as InventoryItem[]);
    runLowStockEmailForItems(pool, "Post-clean inventory");
  };

  // Closeout keys photo upload (moment: post_clean_staging)
  const [keysPhotoUploading, setKeysPhotoUploading] = useState(false);
  const keysPhotoFileRef = useRef<HTMLInputElement>(null);

  const handleUploadKeysPhoto = async (file: File) => {
    if (!file) return;
    if (!propertyId) {
      toast({ title: tDynamic("Property is required to upload."), variant: "destructive" });
      return;
    }
    setKeysPhotoUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const ts = Date.now();
      // Path encodes the moment so future tools can classify by prefix alone.
      const path = `closeout_keys/${propertyId}/${cleaningTaskId ?? "no-task"}/keys_${ts}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("cleaning-photos")
        .upload(path, file, { upsert: false, cacheControl: "3600" });
      if (uploadError) throw uploadError;
      const { data: pub } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
      if (pub?.publicUrl) {
        setCloseoutKeysPhotoUrl(pub.publicUrl);
        toast({ title: tDynamic("Keys photo uploaded.") });
      }
    } catch (err) {
      console.error("[InventoryCheckTab] keys photo upload failed:", err);
      toast({
        title: tDynamic("Could not upload photo. Please try again."),
        variant: "destructive",
      });
    } finally {
      setKeysPhotoUploading(false);
    }
  };

  const handleSubmitFinal = async () => {
    if (submitInventory.isPending || readOnly) return;
    if (needsCloseoutKeys && !keysHandledExternally && !keysReturnedConfirmed) {
      toast({
        title: tDynamic("Confirm keys"),
        description: tDynamic("Confirm that keys were returned before completing."),
        variant: "destructive",
      });
      return;
    }
    try {
      await submitInventory.mutateAsync({
        propertyId,
        items: buildSubmitRows(postCleanItems),
        cleaningTaskId: cleaningTaskId ?? undefined,
        inventorySubmitPhase: "final",
        closeoutOnly: countsPersistedRef.current,
        closeout: {
          keys_returned_confirmed: keysHandledExternally ? true : keysReturnedConfirmed,
          departure_notes: closeoutNotes.trim() || null,
          keys_photo_url: keysHandledExternally
            ? (externalKeysPhotoUrl || closeoutKeysPhotoUrl || null)?.trim() || null
            : closeoutKeysPhotoUrl.trim() || null,
        },
      });

      if (onCompleteCleaning && !readOnly) await onCompleteCleaning();
      if (onInventorySubmitted && !readOnly) onInventorySubmitted();

      // Post-clean counts (guest + supply) then flush one digest.
      runLowStockEmailForItems(postCleanItems, "Post-clean inventory");
      await flushLowStockDigest();

      const lowStockItems = getLowStockItemsForFinish;
      if (lowStockItems.length > 0) {
        toast({
          title: tDynamic("Inventory"),
          description: `${lowStockItems.length} ${tDynamic("item(s) are below threshold.")}`,
        });
      } else {
        toast({ title: tDynamic("Done"), description: tDynamic("Inventory submitted") });
      }
      setShowLowStockFinishConfirm(false);
    } catch (err) {
      console.error("[InventoryCheckTab] handleSubmitFinal failed:", err);
      toast({
        title: tDynamic("Error"),
        description: inventorySubmitErrorMessage(
          err,
          tDynamic("Failed to submit inventory. Check your connection or ask the host to confirm your property access."),
        ),
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    if (submitInventory.isPending) {
      console.warn("[InventoryCheckTab] Submit blocked - already submitting");
      return;
    }
    if (readOnly) {
      console.warn("[InventoryCheckTab] Submit blocked - task is read-only (completed)");
      toast({
        title: tDynamic("Error"),
        description: tDynamic("This inventory check has already been completed and cannot be edited"),
        variant: "destructive",
      });
      return;
    }
    if (taskPhasedMode) return;

    if (
      hasGuestPhaseInventoryOrStaging &&
      guestSetupCompleted === false
    ) {
      toast({
        title: tDynamic("Guest staging required"),
        description: tDynamic("Complete guest amenity staging and photos before submitting inventory."),
        variant: "destructive",
      });
      return;
    }

    try {
      await submitInventory.mutateAsync({
        propertyId,
        items: buildSubmitRows((items ?? []) as InventoryItem[]),
        cleaningTaskId: cleaningTaskId ?? undefined,
      });

      if (onCompleteCleaning && !readOnly) await onCompleteCleaning();
      if (onInventorySubmitted && !readOnly) onInventorySubmitted();

      // Non-phased finish: also build + flush the digest so owners get the single email + inbox row.
      runLowStockEmailForItems((items ?? []) as InventoryItem[], "Post-clean inventory");
      await flushLowStockDigest();

      const lowStockItems = getLowStockItemsForFinish;
      if (lowStockItems.length > 0) {
        toast({
          title: tDynamic("Inventory"),
          description: `${lowStockItems.length} ${tDynamic("item(s) are below threshold.")}`,
        });
      } else {
        toast({ title: tDynamic("Done"), description: tDynamic("Inventory submitted") });
      }
      setShowLowStockFinishConfirm(false);
    } catch (err) {
      console.error("[InventoryCheckTab] handleSubmit failed:", err);
      toast({
        title: tDynamic("Error"),
        description: inventorySubmitErrorMessage(
          err,
          tDynamic("Failed to submit inventory. Check your connection or ask the host to confirm your property access."),
        ),
        variant: "destructive",
      });
    }
  };

  const onFinishClick = () => {
    if (submitInventory.isPending) {
      console.warn("[InventoryCheckTab] Submit blocked - already submitting");
      return;
    }
    if (readOnly) {
      toast({
        title: tDynamic("Error"),
        description: tDynamic("This inventory check has already been completed and cannot be edited"),
        variant: "destructive",
      });
      return;
    }
    if (taskPhasedMode) {
      if (inventoryUiPhase === "counts") {
        if (hasGuestPhaseInventoryOrStaging && guestSetupCompleted === false) {
          toast({
            title: tDynamic("Guest staging required"),
            description: tDynamic("Complete guest amenity staging and photos before submitting inventory."),
            variant: "destructive",
          });
          return;
        }
        if (!allInventoryAccountedFor) return;
        if (getLowStockItemsCurrentPhase.length > 0) setShowLowStockFinishConfirm(true);
        else {
          void (async () => {
            try {
              await persistPostCleanCountsOnly();
              if (keysHandledExternally) {
                // Keys already captured in SimpleKeysStep — finish inventory now
                await handleSubmitFinal();
                return;
              }
              setInventoryUiPhase("keys");
              requestAnimationFrame(() => {
                inventoryPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            } catch (err) {
              console.error("[InventoryCheckTab] persist counts before keys", err);
              toast({
                title: tDynamic("Error"),
                description: tDynamic(
                  "Could not save inventory counts. Check your connection or property access, then try again."
                ),
                variant: "destructive",
              });
            }
          })();
        }
        return;
      }
      if (getLowStockItemsForFinish.length > 0) setShowLowStockFinishConfirm(true);
      else void handleSubmitFinal();
      return;
    }
    if (getLowStockItemsForFinish.length > 0) {
      setShowLowStockFinishConfirm(true);
    } else {
      void handleSubmit();
    }
  };

  if (isLoading || roomsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">{tDynamic("Loading...")}</p>
      </div>
    );
  }

  const handleFinishNoItems = async () => {
    if (submitInventory.isPending || readOnly) return;
    try {
      if (cleaningTaskId && taskPhasedMode && postCleanItems.length === 0) {
        if (!keysHandledExternally && !keysReturnedConfirmed) {
          toast({
            title: tDynamic("Confirm keys"),
            description: tDynamic("Confirm that keys were returned before completing."),
            variant: "destructive",
          });
          return;
        }
        await submitInventory.mutateAsync({
          propertyId,
          items: [],
          cleaningTaskId,
          inventorySubmitPhase: "final",
          closeout: {
            keys_returned_confirmed: keysHandledExternally ? true : keysReturnedConfirmed,
            departure_notes: closeoutNotes.trim() || null,
            keys_photo_url: keysHandledExternally
              ? (externalKeysPhotoUrl || closeoutKeysPhotoUrl || null)?.trim() || null
              : closeoutKeysPhotoUrl.trim() || null,
          },
        });
        if (onCompleteCleaning) await onCompleteCleaning();
        if (onInventorySubmitted) onInventorySubmitted();
        runLowStockEmailForItems(postCleanItems, "Post-clean inventory (closeout)");
        await flushLowStockDigest();
        toast({ title: tDynamic("Done"), description: tDynamic("Task completed") });
        return;
      }

      await submitInventory.mutateAsync({
        propertyId,
        items: [],
        cleaningTaskId: cleaningTaskId ?? undefined,
      });
      if (onCompleteCleaning && !readOnly) await onCompleteCleaning();
      if (onInventorySubmitted && !readOnly) onInventorySubmitted();
      toast({ title: tDynamic("Done"), description: tDynamic("Task completed") });
    } catch {
      toast({ title: tDynamic("Error"), description: tDynamic("Failed to complete"), variant: "destructive" });
    }
  };

  if (!items || items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground mb-4">{tDynamic("No inventory items for this property.")}</p>
          {!readOnly && (
            <Button
              onClick={handleFinishNoItems}
              disabled={submitInventory.isPending}
              className="h-14 w-full bg-[color:var(--pv-teal,hsl(var(--primary)))] text-base font-semibold text-[color:var(--pv-ink-on-accent,hsl(var(--primary-foreground)))] shadow-sm hover:opacity-90 active:scale-[0.99]"
            >
              <Send className="w-5 h-5 mr-2" />
              {submitInventory.isPending ? tDynamic("Saving...") : tDynamic("Finish & Submit Inventory")}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const renderInventoryItem = (item: InventoryItem, _index: number, _totalItems: number, itemNumber: number) => {
    const status = getItemStatus(item);
    const countState = counts.find((c) => c.item_id === item.id);
    const isFullnessItem = item.quantity_type === 'fullness';
    const currentFullness = fullnessValues[item.id] ?? item.current_value ?? 1.0;
    
    return (
      <div
        key={item.id}
        className={cn(
          "mb-5 overflow-hidden rounded-2xl border-2 bg-[color:var(--pv-surface,hsl(var(--card)))] shadow-sm ring-offset-background last:mb-0",
          status === "low" &&
            "border-[color:var(--pv-warn,#c26a1a)]/50 bg-[color:var(--pv-warn-soft,#fbf0e1)] shadow-md dark:bg-[color-mix(in_srgb,var(--pv-warn-soft)_25%,transparent)]",
          status === "out" &&
            "border-[color:var(--pv-error,#c0392b)]/45 bg-red-50/95 shadow-md dark:bg-red-950/25",
          status !== "low" && status !== "out" && "border-[color:var(--pv-line,hsl(var(--border)))]"
        )}
      >
        {/* Item header: stays visually locked to this row */}
        <div
          className={cn(
            "border-b px-3 py-3 sm:px-4 sm:py-3.5",
            status === "low" &&
              "border-[color:var(--pv-warn,#c26a1a)]/30 bg-[color-mix(in_srgb,var(--pv-warn-soft,#fbf0e1)_55%,hsl(var(--card)))]",
            status === "out" && "border-red-200/40 bg-[color-mix(in_srgb,red-50_55%,hsl(var(--card)))] dark:border-red-900/35",
            status !== "low" && status !== "out" && "border-[color:var(--pv-line,hsl(var(--border)))]/70 bg-muted/30"
          )}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {item.photo_url ? (
                <button
                  type="button"
                  onClick={() => setFullscreenPhoto(item.photo_url!)}
                  className="aspect-square w-14 shrink-0 overflow-hidden rounded-lg border border-[color:var(--pv-line,hsl(var(--border)))] bg-background transition-transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--pv-teal-ring,rgba(15,168,154,0.35))]"
                >
                  <img src={item.photo_url} alt={item.item_name} className="h-full w-full object-cover" />
                </button>
              ) : (
                <div className="flex aspect-square w-14 shrink-0 items-center justify-center rounded-lg border border-[color:var(--pv-line,hsl(var(--border)))] bg-background">
                  <Image className="h-5 w-5 text-[color:var(--pv-ink-3,hsl(var(--muted-foreground)))]" aria-hidden />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex h-7 min-w-[2rem] shrink-0 items-center justify-center rounded-md border border-[color:var(--pv-line,hsl(var(--border)))]/80 bg-background px-2 text-xs font-bold tabular-nums text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
                    #{itemNumber}
                  </span>
                  <p className="text-base font-semibold leading-snug tracking-tight break-words text-[color:var(--pv-ink,hsl(var(--foreground)))]">
                    {tDynamic(item.item_name)}
                  </p>
                  {item.vendor_label && (
                    <span className="flex items-center gap-1 rounded border border-[color:var(--pv-line,hsl(var(--border)))] bg-[color:var(--pv-surface-2,hsl(var(--muted)))] px-1.5 py-0.5 text-xs text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                      {item.vendor_label}
                    </span>
                  )}
                </div>
                {isFullnessItem ? (
                  <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
                    {tDynamic("Fullness")}: {getFullnessLabel(item.current_value ?? 1.0)} ({Math.round((item.current_value ?? 1.0) * 100)}%)
                    {item.threshold_value != null ? (
                      <> · {tDynamic("Threshold")}: {getFullnessLabel(item.threshold_value)} ({Math.round(item.threshold_value * 100)}%)</>
                    ) : (
                      <> · {tDynamic("Threshold")}: —</>
                    )}
                  </p>
                ) : (
                  <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
                    {tDynamic("In stock")}: {getInStockCount(item as InventoryItem)}
                    {(getOrderedCount(item as InventoryItem) ?? 0) > 0 && (
                      <> · {tDynamic("Ordered (not arrived)")}: {getOrderedCount(item as InventoryItem)}</>
                    )}
                    <> · {tDynamic("Threshold")}: {item.threshold_count != null ? item.threshold_count : "—"}</>
                  </p>
                )}
                {status === "out" && (
                  <p className="mt-1.5 text-xs font-semibold text-red-700 dark:text-red-300">
                    {tDynamic("Out of stock — refill required")}
                  </p>
                )}
                {status === "low" && (
                  <p className="mt-1.5 text-xs font-semibold text-[color:var(--pv-warn,#c26a1a)]">
                    {tDynamic("Below threshold — restock recommended")}
                  </p>
                )}
              </div>
            </div>
            <StatusBadge status={status} tDynamic={tDynamic} />
          </div>
        </div>

        {/* (1) Update count first, then (2) Request — grouped so the next card’s header never blends in */}
        {!readOnly && (
          <div className="space-y-4 p-3 sm:p-4">
            <div className="rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))]/80 bg-muted/15 p-3 sm:p-4">
              {isFullnessItem ? (
                <div className="space-y-3">
                  <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {tDynamic("Update fullness")}
                  </Label>
                  <div className={cn(
                    "grid gap-2",
                    keysHandledExternally ? "grid-cols-5" : "grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap"
                  )}>
                    {FULLNESS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleFullnessChange(item.id, option.value)}
                        className={cn(
                          "min-h-[48px] rounded-lg px-2 py-3 text-center text-sm font-bold transition-all duration-150 active:scale-[0.98]",
                          keysHandledExternally && "rounded-[14px] text-[15px]",
                          currentFullness === option.value
                            ? "bg-[color:var(--pv-teal,hsl(var(--primary)))] text-[color:var(--pv-ink-on-accent,white)] ring-2 ring-[color:var(--pv-teal-ring,rgba(15,168,154,0.35))] ring-offset-2 ring-offset-background"
                            : "bg-[color:var(--pv-surface-2,hsl(var(--muted)))] text-[color:var(--pv-ink,hsl(var(--foreground)))] hover:bg-[color-mix(in_srgb,var(--pv-surface-2,hsl(var(--muted)))_85%,transparent)]"
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {!keysHandledExternally && (
                  <div className="flex flex-wrap items-center gap-2 border-t border-[color:var(--pv-line,hsl(var(--border)))]/60 pt-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 border-red-300 bg-red-50 text-xs font-medium text-red-800 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100 dark:hover:bg-red-950/60"
                      disabled={readOnly}
                      onClick={() => {
                        handleFullnessChange(item.id, 0);
                        toast({ title: tDynamic("Marked missing / needs refill") });
                      }}
                    >
                      {tDynamic("Mark missing / needs refill")}
                    </Button>
                    {accountedFor[item.id] !== "no_change" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 border-emerald-300 bg-emerald-50 text-xs font-medium text-emerald-900 hover:bg-emerald-100/90 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-50 dark:hover:bg-emerald-950/55"
                        disabled={readOnly}
                        onClick={() => markNoChange(item.id)}
                      >
                        {tDynamic("No change in count")}
                      </Button>
                    )}
                  </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {tDynamic("Update in-stock count")}
                  </Label>
                  {(() => {
                    const cur = countState?.count ?? getInStockCount(item as InventoryItem);
                    return (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-11 w-11 shrink-0 rounded-full border-[color:var(--pv-line,hsl(var(--border)))]"
                        disabled={readOnly}
                        onClick={() => updateCount(item.id, Math.max(0, cur - 1))}
                        aria-label={tDynamic("Decrease count")}
                      >
                        <Minus className="h-4 w-4" aria-hidden />
                      </Button>
                    <Input
                      type="number"
                      min={0}
                      value={cur}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "" || raw === "-") {
                          updateCount(item.id, 0);
                          return;
                        }
                        const n = parseInt(raw, 10);
                        if (!Number.isNaN(n)) updateCount(item.id, Math.max(0, n));
                      }}
                      onFocus={(e) => e.target.select()}
                      className="h-11 w-[4.5rem] rounded-xl border-[color:var(--pv-line,hsl(var(--border)))] bg-background text-center text-lg font-semibold tabular-nums"
                      inputMode="numeric"
                    />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-11 w-11 shrink-0 rounded-full border-[color:var(--pv-line,hsl(var(--border)))]"
                        disabled={readOnly}
                        onClick={() => updateCount(item.id, Math.min(9999, cur + 1))}
                        aria-label={tDynamic("Increase count")}
                      >
                        <Plus className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 border-red-300 bg-red-50 text-xs font-medium text-red-800 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100 dark:hover:bg-red-950/60"
                      disabled={readOnly}
                      onClick={() => {
                        updateCount(item.id, 0);
                        toast({ title: tDynamic("Marked missing / needs refill") });
                      }}
                    >
                      {tDynamic("Mark missing / needs refill")}
                    </Button>
                    {accountedFor[item.id] !== "no_change" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 border-emerald-300 bg-emerald-50 text-xs font-medium text-emerald-900 hover:bg-emerald-100/90 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-50 dark:hover:bg-emerald-950/55"
                        disabled={readOnly}
                        onClick={() => markNoChange(item.id)}
                      >
                        {tDynamic("No change in count")}
                      </Button>
                    )}
                  </div>
                    );
                  })()}
                  {!isFullnessItem && item.threshold_count != null && (countState?.count ?? getInStockCount(item as InventoryItem)) <= item.threshold_count && (
                    <p className="text-xs font-medium leading-snug text-[color:var(--pv-warn,#c26a1a)]">
                      {tDynamic("Below threshold. Choose Request owner to order or I purchased supplies.")}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-background p-2 sm:p-2.5">
              {(() => {
                const existingReq = requestOrderByItemId.get(item.id);
                if (existingReq) {
                  return (
                    <div className="flex h-12 w-full items-center gap-3 rounded-lg border border-emerald-200/80 bg-emerald-50/90 px-3 py-2 sm:h-11 dark:border-emerald-800/50 dark:bg-emerald-950/35">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                      <div className="min-w-0 text-left">
                        <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                          {tDynamic("Request made")}
                        </p>
                        <p className="text-xs text-emerald-800/90 dark:text-emerald-200/90">
                          {tDynamic("Owner was notified.")}{" "}
                          {typeof existingReq.quantity === "number" && existingReq.quantity > 0
                            ? `${tDynamic("Qty")} ${existingReq.quantity}.`
                            : null}
                        </p>
                      </div>
                    </div>
                  );
                }
                return (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full justify-start gap-3 rounded-lg border-transparent px-3 transition-transform active:scale-[0.99] hover:bg-muted/40 sm:h-11"
                    onClick={() => openRequestSheet(item)}
                  >
                    <ShoppingCart className="w-4 h-4 shrink-0" />
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium">{tDynamic("Request owner to order")}</p>
                      <p className="text-xs text-muted-foreground">{tDynamic("Notify owner to restock this item")}</p>
                    </div>
                  </Button>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Check if we have rooms
  const hasRooms = rooms && rooms.length > 0;
  const hasUnassigned = groupedItems.unassigned.length > 0;

  return (
    <div
      ref={inventoryPanelRef}
      className={cn(
        "space-y-4",
        inventoryFlowMode === "single" && "space-y-3",
        // Mobile: sticky CTA (keys + finish) sits above content; reserve scroll space so last controls stay tappable.
        taskPhasedMode &&
          cleaningTaskId &&
          !readOnly &&
          "max-md:pb-[calc(12rem+env(safe-area-inset-bottom,0px))] md:pb-2"
      )}
    >
      <div className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          {tDynamic("Inventory & closeout")}
        </h2>
        {propertySubtitle ? (
          <p className="text-sm text-muted-foreground leading-snug">{propertySubtitle}</p>
        ) : null}
      </div>
      {taskPhasedMode && !hidePhasedInstructionBanner && (
        <Card
          className={cn(
            "overflow-hidden border-l-4",
            inventoryFlowMode === "split_guest" &&
              "border-[color:var(--pv-line,hsl(var(--border)))] border-l-[color:var(--pv-teal,hsl(var(--primary)))] bg-[color:var(--pv-teal-soft,rgb(230_244_242))] shadow-sm",
            inventoryFlowMode === "split_supply" &&
              "border border-[color:var(--pv-line,hsl(var(--border)))] border-l-[color:#64748b] bg-slate-100/85 shadow-sm dark:border-l-slate-400 dark:bg-slate-950/45",
            inventoryFlowMode === "single" &&
              "border border-dashed border-[color:var(--pv-line,hsl(var(--border)))] border-l-[color:var(--pv-teal,hsl(var(--primary)))] bg-muted/15 shadow-none"
          )}
        >
          <CardContent
            className={cn(
              "text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]",
              inventoryFlowMode === "single" ? "py-2.5 text-xs sm:text-sm" : "py-3 text-sm"
            )}
          >
            {inventoryUiPhase === "counts" ? (
              <p>
                <span className="font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">
                  {tDynamic("Inventory")}
                </span>{" "}
                —{" "}
                {tDynamic(
                  "Count each stock line in the unit (wherever the host keeps guest and cleaning supplies). When finished, tap Continue to key drop-off.",
                )}
              </p>
            ) : (
              <p>
                <span className="font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">
                  {tDynamic("Key drop-off")}
                </span>{" "}
                — {tDynamic("Confirm keys are returned or secured, add an optional photo, then complete the turnover.")}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {taskPhasedMode &&
        displaySource.length === 0 &&
        !hidePhasedEmptyInventoryCard &&
        inventoryUiPhase === "counts" && (
          <Card className="border-2 border-dashed border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/10">
            <CardContent className="space-y-4 py-8 text-center text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
              <Package
                className="mx-auto h-10 w-10 text-[color:var(--pv-teal,hsl(var(--primary)))] opacity-70"
                aria-hidden
              />
              <p>
                {guestStagingLineCountSafe > 0 && guestSetupCompleted === false
                  ? tDynamic("Complete guest amenity staging and photos (previous step), then continue here.")
                  : tDynamic("No stock rows to count for this turnover. You can continue to key drop-off.")}
              </p>
              <Button
                type="button"
                className="w-full bg-[color:var(--pv-teal,hsl(var(--primary)))] font-semibold text-[color:var(--pv-ink-on-accent,hsl(var(--primary-foreground)))] hover:opacity-90"
                onClick={() => {
                  if (guestStagingLineCountSafe > 0 && guestSetupCompleted === false) {
                    toast({
                      title: tDynamic("Guest staging required"),
                      description: tDynamic("Finish the guest amenities step first, then return here."),
                      variant: "destructive",
                    });
                    return;
                  }
                  setInventoryUiPhase("keys");
                  requestAnimationFrame(() => {
                    inventoryPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  });
                }}
                disabled={submitInventory.isPending}
              >
                {tDynamic("Continue to key drop-off")}
              </Button>
            </CardContent>
          </Card>
        )}

      {(!taskPhasedMode || inventoryUiPhase === "counts") && (
        hasRooms ? (
        // Grouped by rooms - Accordion view
        <Accordion type="multiple" defaultValue={rooms.map(r => r.id)} className="space-y-3">
          {rooms.map((room) => {
            const roomItems = groupedItems.byRoom[room.id] || [];
            if (roomItems.length === 0) return null;

            const sev = getRoomSeverityCounts(roomItems);

            return (
              <AccordionItem
                key={room.id}
                value={room.id}
                className={cn(
                  "overflow-hidden rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-card shadow-sm",
                  inventoryFlowMode === "split_supply" && "border-slate-300/80 dark:border-slate-600/60"
                )}
              >
                <AccordionTrigger className="px-3 py-2.5 hover:no-underline hover:bg-muted/50 sm:px-4 sm:py-3">
                  <div className="flex w-full min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      {inventoryFlowMode === "split_supply" ? (
                        <Archive className="h-5 w-5 shrink-0 text-slate-600 dark:text-slate-300" aria-hidden />
                      ) : (
                        <Package className="h-5 w-5 shrink-0 text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]" aria-hidden />
                      )}
                      <span className="truncate font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">{room.name}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:shrink-0 sm:justify-end">
                      <Badge
                        variant="secondary"
                        className="rounded-full border border-[color:var(--pv-line,hsl(var(--border)))]/90 bg-muted/60 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-foreground"
                      >
                        {sev.total} {tDynamic("items")}
                      </Badge>
                      {sev.out > 0 && (
                        <Badge
                          variant="secondary"
                          className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100"
                        >
                          <AlertTriangle className="mr-0.5 inline h-3 w-3 align-text-bottom" aria-hidden />
                          {sev.out} {tDynamic("out")}
                        </Badge>
                      )}
                      {sev.low > 0 && (
                        <Badge
                          variant="secondary"
                          className="rounded-full border border-amber-300/80 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/35 dark:text-amber-100"
                        >
                          <AlertTriangle className="mr-0.5 inline h-3 w-3 align-text-bottom" aria-hidden />
                          {sev.low} {tDynamic("low")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-3 pt-2 sm:px-3 sm:pb-4 sm:pt-2">
                  {(room.entry_code?.trim() || room.entry_notes?.trim()) && (
                    <Collapsible className="mx-0 mb-3 mt-1 overflow-hidden rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-card sm:mt-2">
                      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/40">
                        <span className="flex min-w-0 items-center gap-2">
                          <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="truncate">{tDynamic("Code / access for this room")}</span>
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1.5 border-t border-[color:var(--pv-line,hsl(var(--border)))]/80 bg-muted/20 px-3 py-3 text-sm">
                        {room.entry_code?.trim() && <p className="text-foreground">{room.entry_code.trim()}</p>}
                        {room.entry_notes?.trim() && (
                          <p className="whitespace-pre-wrap text-muted-foreground">{room.entry_notes.trim()}</p>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                  {roomItems.map((item, index) => renderInventoryItem(item, index, roomItems.length, index + 1))}
                </AccordionContent>
              </AccordionItem>
            );
          })}
          
          {/* Unassigned items */}
          {hasUnassigned && (
            <AccordionItem
              value="unassigned"
              className={cn(
                "overflow-hidden rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-card shadow-sm",
                inventoryFlowMode === "split_supply" && "border-slate-300/80 dark:border-slate-600/60"
              )}
            >
              <AccordionTrigger className="px-3 py-2.5 hover:no-underline hover:bg-muted/50 sm:px-4 sm:py-3">
                {(() => {
                  const u = groupedItems.unassigned;
                  const sev = getRoomSeverityCounts(u);
                  return (
                    <div className="flex w-full min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        {inventoryFlowMode === "split_supply" ? (
                          <Archive className="h-5 w-5 shrink-0 text-slate-600 dark:text-slate-300" aria-hidden />
                        ) : (
                          <Package className="h-5 w-5 shrink-0 text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]" aria-hidden />
                        )}
                        <span className="truncate font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">{tDynamic("Other Items")}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 sm:shrink-0 sm:justify-end">
                        <Badge
                          variant="secondary"
                          className="rounded-full border border-[color:var(--pv-line,hsl(var(--border)))]/90 bg-muted/60 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-foreground"
                        >
                          {sev.total} {tDynamic("items")}
                        </Badge>
                        {sev.out > 0 && (
                          <Badge
                            variant="secondary"
                            className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100"
                          >
                            <AlertTriangle className="mr-0.5 inline h-3 w-3 align-text-bottom" aria-hidden />
                            {sev.out} {tDynamic("out")}
                          </Badge>
                        )}
                        {sev.low > 0 && (
                          <Badge
                            variant="secondary"
                            className="rounded-full border border-amber-300/80 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/35 dark:text-amber-100"
                          >
                            <AlertTriangle className="mr-0.5 inline h-3 w-3 align-text-bottom" aria-hidden />
                            {sev.low} {tDynamic("low")}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-3 pt-2 sm:px-3 sm:pb-4 sm:pt-2">
                {groupedItems.unassigned.map((item, index) => 
                  renderInventoryItem(item, index, groupedItems.unassigned.length, index + 1)
                )}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      ) : (
        // Flat list - no rooms
        <Card
          className={cn(
            "overflow-hidden rounded-xl border-[color:var(--pv-line,hsl(var(--border)))] shadow-sm",
            inventoryFlowMode === "single" && "border border-dashed"
          )}
        >
          <CardHeader className="border-b border-[color:var(--pv-divider,hsl(var(--border)))] pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-[color:var(--pv-ink,hsl(var(--foreground)))] sm:text-lg">
              <Package className="h-5 w-5 text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]" aria-hidden />
              {tDynamic("Inventory")}
            </CardTitle>
            <p className="text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">{tDynamic("Update the counts for each item")}</p>
          </CardHeader>
          <CardContent className="space-y-0 px-2 pb-4 pt-2 sm:px-3">
            {displaySource.map((item, index) => {
              const typedItem = item as InventoryItem;
              return renderInventoryItem(typedItem, index, displaySource.length, index + 1);
            })}
          </CardContent>
        </Card>
      ))}

      {/* Single CTA: I purchased supplies — hidden on key drop-off (counts phase only when phased) */}
      {!readOnly &&
        items &&
        items.length > 0 &&
        showPurchasesAndCleanerBuys &&
        (!taskPhasedMode || inventoryUiPhase === "counts") && (
        <Button
          type="button"
          variant="outline"
          className="h-auto min-h-[3.5rem] w-full flex-col items-stretch gap-0 rounded-2xl border-[color:var(--pv-line,hsl(var(--border)))] bg-card p-4 text-left shadow-sm transition-transform active:scale-[0.99] hover:bg-muted/30"
          onClick={() => setPurchaseModeModalOpen(true)}
        >
          <span className="flex items-start gap-3">
            <Receipt className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--pv-teal,#0fa89a)]" />
            <span className="min-w-0 space-y-0.5">
              <span className="block font-semibold text-foreground">{tDynamic("I purchased supplies")}</span>
              <span className="block text-xs text-muted-foreground">
                {tDynamic("Log a receipt or single item for reimbursement")}
              </span>
            </span>
          </span>
        </Button>
      )}

      {/* Purchases for this turnover — hidden on key drop-off (counts phase only when phased) */}
      {cleaningTaskId &&
        (turnoverPurchases.length > 0 || taskReimbursements.length > 0 || !readOnly) &&
        showPurchasesAndCleanerBuys &&
        (!taskPhasedMode || inventoryUiPhase === "counts") && (
        <Accordion
          type="multiple"
          defaultValue={turnoverPurchases.length > 0 || taskReimbursements.length > 0 ? ["purchases"] : []}
          className="mt-4"
        >
          <AccordionItem value="purchases" className="border rounded-lg overflow-hidden">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {tDynamic("Purchases for this turnover")} ({turnoverPurchases.length + taskReimbursements.length})
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 pt-0">
              {!readOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit mb-3"
                  onClick={() => {
                    setReimbursementPrefillName("");
                    setReimbursementModalOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {tDynamic("Add reimbursement")}
                </Button>
              )}
              {turnoverPurchases.length > 0 && (
                <ul className="space-y-2 mb-3">
                  {turnoverPurchases.map((p) => {
                    const itemLabel = p.item_name ?? (p.line_items?.length ? p.line_items.map((li) => `${li.item_name ?? "Item"} × ${li.quantity}`).join(", ") : null) ?? "—";
                    const roomName = rooms?.find((r) => r.id === p.room_id)?.name ?? null;
                    const statusLabel = getTurnoverPurchaseStatusLabel(p as { status: string; type: string; payment_failed?: boolean }, tDynamic);
                    return (
                      <li
                        key={p.id}
                        className="flex flex-wrap items-center justify-between gap-2 text-sm py-3 px-3 rounded-lg border bg-card"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{itemLabel}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-muted-foreground">
                            {p.item_id != null && <span>{tDynamic("Qty")}: {p.quantity}</span>}
                            {p.type === "cleaner_purchase" && p.amount > 0 && (
                              <span>${Number(p.amount).toFixed(2)}</span>
                            )}
                            {p.type === "request_order" && <span>{tDynamic("Request")}</span>}
                            <Badge variant="secondary" className="text-xs font-normal">
                              {statusLabel}
                            </Badge>
                            {roomName && <span className="text-xs">{roomName}</span>}
                          </div>
                          {p.receipt_url && (
                            <a
                              href={p.receipt_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary mt-1"
                            >
                              <Receipt className="w-3 h-3" />
                              {tDynamic("Receipt")}
                            </a>
                          )}
                        </div>
                        {!readOnly && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditingPurchase(p)}
                              title={tDynamic("Edit")}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setRemoveConfirmPurchase(p)}
                              title={tDynamic("Remove")}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {taskReimbursements.length > 0 && (
                <ul className="space-y-2">
                  {taskReimbursements.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between text-sm py-2 border-b last:border-0"
                    >
                      <span className="font-medium truncate">{r.description || `${(r.amount_cents / 100).toFixed(2)}`}</span>
                      <span className="text-muted-foreground whitespace-nowrap ml-2">
                        ${(r.amount_cents / 100).toFixed(2)}
                        {r.status === "paid" && r.paid_at && (
                          <span className="text-xs ml-1">({tDynamic("Paid")})</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {turnoverPurchases.length === 0 && taskReimbursements.length === 0 && !readOnly && (
                <p className="text-sm text-muted-foreground">{tDynamic("No purchases logged yet.")}</p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {!readOnly && !cleaningTaskId && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full mt-2"
          onClick={() => {
            setReimbursementPrefillName("");
            setReimbursementModalOpen(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          {tDynamic("Add reimbursement")}
        </Button>
      )}

      {taskPhasedMode && inventoryUiPhase === "keys" && !readOnly && (
        <Card className="overflow-hidden rounded-2xl border border-[color:var(--pv-line,hsl(var(--border)))] border-l-4 border-l-[color:var(--pv-teal,hsl(var(--primary)))] shadow-md">
          <CardHeader className="space-y-1 border-b border-[color:var(--pv-divider,hsl(var(--border)))] bg-[color:var(--pv-surface-2,hsl(var(--muted)))]/35 pb-3 pt-4">
            <div className="flex items-start gap-3">
              <KeyRound className="mt-0.5 h-6 w-6 shrink-0 text-[color:var(--pv-teal,hsl(var(--primary)))]" aria-hidden />
              <div className="min-w-0 space-y-1">
                <CardTitle className="text-base text-[color:var(--pv-ink,hsl(var(--foreground)))]">
                  {tDynamic("Keys & departure")}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <div className="border-b border-[color:var(--pv-divider,hsl(var(--border)))] px-4 pb-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 -ml-2 text-muted-foreground"
              onClick={() => setInventoryUiPhase("counts")}
            >
              {tDynamic("Back to inventory counts")}
            </Button>
          </div>
          <CardContent className="space-y-5 pt-5">
            <div
              className={cn(
                "flex items-center justify-between gap-3 rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-card p-4 transition-shadow",
                needsCloseoutKeys && !keysReturnedConfirmed && "ring-2 ring-[color:var(--pv-warn,#c26a1a)]/30"
              )}
            >
              <div className="min-w-0">
                <Label htmlFor="keys-returned" className="cursor-pointer text-[color:var(--pv-ink,hsl(var(--foreground)))]">
                  {tDynamic("Keys returned / secured")}
                </Label>
                <p className="mt-1 text-xs leading-relaxed text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
                  {tDynamic("Required to finish the turnover.")}
                </p>
              </div>
              <Switch id="keys-returned" checked={keysReturnedConfirmed} onCheckedChange={setKeysReturnedConfirmed} />
            </div>
            <div className="space-y-2">
              <Label className="text-[color:var(--pv-ink,hsl(var(--foreground)))]">{tDynamic("Departure notes (optional)")}</Label>
              <Textarea
                value={closeoutNotes}
                onChange={(e) => setCloseoutNotes(e.target.value)}
                rows={3}
                className="min-h-[5.5rem] border-[color:var(--pv-line,hsl(var(--border)))] bg-background text-[color:var(--pv-ink,hsl(var(--foreground)))]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[color:var(--pv-ink,hsl(var(--foreground)))]">
                {tDynamic("Keys photo (optional)")}
              </Label>
              <p className="text-xs text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
                {tDynamic("Snap a photo of keys returned or back in the lockbox. Saved with this turnover report.")}
              </p>
              <input
                ref={keysPhotoFileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleUploadKeysPhoto(f);
                  e.target.value = "";
                }}
              />
              {closeoutKeysPhotoUrl ? (
                <div className="flex items-start gap-3 rounded-lg border border-[color:var(--pv-line,hsl(var(--border)))] bg-background p-2">
                  <button
                    type="button"
                    onClick={() => setFullscreenPhoto(closeoutKeysPhotoUrl)}
                    className="block h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-muted"
                    aria-label={tDynamic("View keys photo")}
                  >
                    <img
                      src={closeoutKeysPhotoUrl}
                      alt={tDynamic("Keys")}
                      className="h-full w-full object-cover"
                    />
                  </button>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Badge
                      variant="outline"
                      className="w-fit border-emerald-300 bg-emerald-50 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
                      title={tDynamic("Keys confirmed and secured.")}
                    >
                      {tDynamic("Keys photo")}
                    </Badge>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => keysPhotoFileRef.current?.click()}
                        disabled={keysPhotoUploading || readOnly}
                      >
                        {tDynamic("Replace")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setCloseoutKeysPhotoUrl("")}
                        disabled={keysPhotoUploading || readOnly}
                      >
                        {tDynamic("Remove")}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => keysPhotoFileRef.current?.click()}
                  disabled={keysPhotoUploading || readOnly}
                  className="h-12 w-full gap-2 border-dashed border-[color:var(--pv-line,hsl(var(--border)))] text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]"
                >
                  <Camera className="h-4 w-4" />
                  {keysPhotoUploading ? tDynamic("Uploading…") : tDynamic("Add keys photo")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!readOnly && (
        <div
          className={cn(
            "mt-6 space-y-2",
            taskPhasedMode &&
              cleaningTaskId &&
              "sticky bottom-16 z-20 -mx-1 rounded-t-xl border border-[color:var(--pv-line,hsl(var(--border)))] border-b-0 bg-[color-mix(in_srgb,var(--pv-surface,hsl(var(--background)))_92%,transparent)] p-3 pb-4 pt-3 shadow-[0_-8px_24px_rgba(15,23,28,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-[color-mix(in_srgb,var(--pv-surface,hsl(var(--background)))_86%,transparent)] md:static md:z-auto md:mx-0 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:pb-0 md:shadow-none md:backdrop-blur-none"
          )}
        >
          {!canCompleteCleaning && (
            <div
              className="flex gap-2.5 rounded-xl border border-amber-300/70 bg-amber-50 px-3 py-2.5 text-sm text-amber-950 shadow-sm dark:border-amber-700/40 dark:bg-amber-950/30 dark:text-amber-50"
              role="status"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              <span className="leading-snug">
                {!allTasksCompleted
                  ? tDynamic("Complete all checklist tasks before finishing.")
                  : inventoryUiPhase === "keys" && !keysReturnedConfirmed
                    ? tDynamic("Confirm keys returned to finish.")
                    : inventoryUiPhase === "counts" && !allInventoryAccountedFor
                      ? tDynamic("Account for each item: update count or tap “No change in count”.")
                      : inventoryUiPhase === "counts" && !guestStagingGateOk
                        ? tDynamic("Complete guest amenity staging before continuing.")
                        : null}
              </span>
            </div>
          )}
          <Button
            onClick={onFinishClick}
            disabled={submitInventory.isPending || !canCompleteCleaning}
            className={cn(
              "h-14 w-full rounded-2xl bg-[color:var(--pv-teal,hsl(var(--primary)))] text-base font-semibold text-[color:var(--pv-ink-on-accent,hsl(var(--primary-foreground)))] shadow-sm transition-transform hover:opacity-90 active:scale-[0.99] disabled:opacity-50",
              taskPhasedMode &&
                inventoryUiPhase === "keys" &&
                canCompleteCleaning &&
                !submitInventory.isPending &&
                "ring-2 ring-[color:var(--pv-teal)]/35 ring-offset-2 ring-offset-background"
            )}
          >
            <Send className="w-5 h-5 mr-2" />
            {submitInventory.isPending
              ? tDynamic("Saving...")
              : taskPhasedMode && inventoryUiPhase === "counts"
                ? tDynamic("Continue to key drop-off")
                : keysHandledExternally
                  ? "Next"
                  : cleaningTaskId
                    ? tDynamic("Complete Cleaning")
                    : tDynamic("Finish & Submit Inventory")}
          </Button>
        </div>
      )}

      <AlertDialog open={showLowStockFinishConfirm} onOpenChange={setShowLowStockFinishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tDynamic("Some items are low")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tDynamic("These items are low. Do you want to finish the cleaning without reporting, purchasing, or refilling inventory?")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tDynamic("No, go back")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                if (taskPhasedMode && inventoryUiPhase === "counts") {
                  e.preventDefault();
                  try {
                    await persistPostCleanCountsOnly();
                    setShowLowStockFinishConfirm(false);
                    setInventoryUiPhase("keys");
                    requestAnimationFrame(() => {
                      inventoryPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    });
                  } catch (err) {
                    console.error("[InventoryCheckTab] persist counts (low-stock confirm)", err);
                    toast({
                      title: tDynamic("Error"),
                      description: tDynamic(
                        "Could not save inventory counts. Check your connection or property access, then try again."
                      ),
                      variant: "destructive",
                    });
                  }
                  return;
                }
                setShowLowStockFinishConfirm(false);
                if (taskPhasedMode && inventoryUiPhase === "keys") void handleSubmitFinal();
                else void handleSubmit();
              }}
            >
              {tDynamic("Yes, finish cleaning")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReimbursementModal
        open={reimbursementModalOpen}
        onOpenChange={setReimbursementModalOpen}
        propertyId={propertyId}
        cleaningTaskId={cleaningTaskId ?? null}
        createdByUserId={user?.id ?? ""}
        billToType={billToType as "owner_to_cleaner" | "owner_to_company" | "none"}
        billToId={billToId}
        inventoryAutopayEnabled={inventoryAutopayEnabled}
        defaultItemName={reimbursementPrefillName}
      />

      {/* Fullscreen photo modal */}
      <Dialog open={!!fullscreenPhoto} onOpenChange={() => setFullscreenPhoto(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/90 border-none">
          <button
            onClick={() => setFullscreenPhoto(null)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {fullscreenPhoto && (
            <img
              src={fullscreenPhoto}
              alt="Inventory item"
              className="w-full h-full max-h-[90vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      <InventoryItemSheet
        item={selectedItem}
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) setSheetInitialMode("idle");
          setSheetOpen(open);
        }}
        initialActionMode={sheetInitialMode}
        existingRequestOrder={
          selectedItem ? requestOrderByItemId.get(selectedItem.id) ?? null : null
        }
        propertyId={propertyId}
        cleanerId={cleanerId}
        reimbursementMode={reimbursementMode}
        cleaningTaskId={cleaningTaskId}
        scheduledDate={scheduledDate}
      />

      <PurchaseModeModal
        open={purchaseModeModalOpen}
        onOpenChange={setPurchaseModeModalOpen}
        onSelect={handlePurchaseModeSelect}
      />
      <MultiItemPurchaseSheet
        open={multiItemSheetOpen}
        onOpenChange={setMultiItemSheetOpen}
        propertyId={propertyId}
        cleanerId={cleanerId}
        cleaningTaskId={cleaningTaskId ?? null}
        scheduledDate={scheduledDate ?? null}
      />
      <PurchaseItemPickerSheet
        open={purchaseItemPickerOpen}
        onOpenChange={setPurchaseItemPickerOpen}
        items={items ?? []}
        onSelect={handlePurchaseItemPick}
      />

      <EditTurnoverPurchaseSheet
        open={!!editingPurchase}
        onOpenChange={(open) => !open && setEditingPurchase(null)}
        purchase={editingPurchase}
        propertyId={propertyId}
        cleaningTaskId={cleaningTaskId}
      />

      <AlertDialog open={!!removeConfirmPurchase} onOpenChange={(open) => !open && setRemoveConfirmPurchase(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tDynamic("Remove purchase?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tDynamic("This will remove this entry from the list. You can add a new purchase if needed.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tDynamic("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!removeConfirmPurchase) return;
                try {
                  await deletePurchase.mutateAsync({
                    purchaseId: removeConfirmPurchase.id,
                    propertyId,
                    cleaningTaskId: cleaningTaskId ?? undefined,
                  });
                  toast({ title: tDynamic("Removed") });
                  setRemoveConfirmPurchase(null);
                } catch {
                  toast({ title: tDynamic("Error"), variant: "destructive" });
                }
              }}
              disabled={deletePurchase.isPending}
            >
              {deletePurchase.isPending ? tDynamic("Removing...") : tDynamic("Remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InventoryCheckTab;
